import { Box, Checkbox, Group, Stack, TextInput, Tooltip } from '@mantine/core';
import { getHotkeyHandler } from '@mantine/hooks';
import { Switch } from '@repo/foundations';
import { useContext, useState } from 'react';
import { GLOBAL_SHORTCUTS } from '../../constants/shortcuts';
import { type FeatureFlags } from '../../utils/featureFlags';
import { FeatureFlagsContext } from '../../utils/featureFlags/FeatureFlagsContextStore';
import { DefaultDevDropdown } from './DefaultDevDropdown';
import type { DevPanelItemProps } from './DevPanel';

function FeatureFlagSwitch({ flag }: { flag: keyof FeatureFlags }) {
	const {
		featureFlags,
		featureFlagsOverrides,
		ldFeatureFlags,
		setOverride,
		clearOverride,
	} = useContext(FeatureFlagsContext);

	const isOverriden = featureFlagsOverrides[flag] !== undefined;

	return (
		<Group>
			<Tooltip label={isOverriden ? 'Disable override' : 'Enable override'}>
				<Checkbox
					checked={isOverriden}
					onChange={() =>
						isOverriden
							? clearOverride(flag)
							: setOverride(flag, !ldFeatureFlags[flag])
					}
				/>
			</Tooltip>
			<Tooltip
				label={`Default value: ${ldFeatureFlags[flag]}, current value: ${featureFlags[flag]}`}
			>
				<Box>
					<Switch
						label={flag}
						labelPosition="left"
						disabled={!isOverriden}
						checked={featureFlags[flag]}
						onChange={(event) => setOverride(flag, event.currentTarget.checked)}
					/>
				</Box>
			</Tooltip>
		</Group>
	);
}

export function FeatureFlags({ close }: DevPanelItemProps) {
	const { featureFlags, featureFlagsOverrides } =
		useContext(FeatureFlagsContext);
	const [search, setSearch] = useState('');

	const filteredFlags = Object.keys(featureFlags)
		.filter((flag) => flag.toLowerCase().includes(search.toLowerCase()))
		.sort((a, b) => {
			const isOverridenA =
				featureFlagsOverrides[a as keyof FeatureFlags] !== undefined;
			const isOverridenB =
				featureFlagsOverrides[b as keyof FeatureFlags] !== undefined;
			if (isOverridenA && !isOverridenB) {
				return -1;
			}
			if (!isOverridenA && isOverridenB) {
				return 1;
			}

			return 0;
		});

	return (
		<DefaultDevDropdown label="Feature Flags" close={close}>
			<Stack pr="md" style={{ maxHeight: '500px', overflowY: 'auto' }}>
				<TextInput
					placeholder="Search feature flags"
					value={search}
					onChange={(event) => setSearch(event.currentTarget.value)}
					autoFocus
					onKeyDown={getHotkeyHandler([[GLOBAL_SHORTCUTS.ESCAPE[0], close]])}
				/>
				{filteredFlags.map((flag) => (
					<FeatureFlagSwitch key={flag} flag={flag as keyof FeatureFlags} />
				))}
			</Stack>
		</DefaultDevDropdown>
	);
}
